import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { SelectionId } from 'src_common/common/websocket2/id/WebsocketId';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { BreakpointsState } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';
import {
    getBoostedPrice,
    getHorseRacingRunner,
    getSP,
    getTcFc,
    getWinner,
} from 'src/domains/sportsbook/webview/components/raceSummary/utils/Utils';
import { RaceSummaryRowHorseRacing } from 'src/domains/sportsbook/webview/components/raceSummary/raceSummaryRowSport/horseRacing/RaceSummaryRowHorseRacing';
import { RaceSummaryCastRowHorseRacing } from 'src/domains/sportsbook/webview/components/raceSummary/raceSummaryRowSport/horseRacing/RaceSummaryCastRowHorseRacing';

interface HorseRacingRowPropsType {
    selectionId: SelectionId;
    type?: string;
    isSpecials?: boolean;
    isRaceInProgress: boolean;
    isPriceHistoryHidden?: boolean;
    isPriceBoostActive?: boolean;
}

export const HorseRacingRow = observer('RaceSummaryRow', (props: HorseRacingRowPropsType) => {
    const { selectionId, type, isSpecials, isRaceInProgress, isPriceHistoryHidden, isPriceBoostActive } = props;
    const common = useCommon();
    const breakpointsState = BreakpointsState.get(common);
    const configState = ConfigComponents.get(common).config;
    const { sportsbookVariant } = configState.componentVariants;

    const selectionModel = selectionId.getModel();
    if (selectionModel === null) {
        return null;
    }

    const marketModel = selectionModel.getMarket();
    if (marketModel === null) {
        return null;
    }

    const eventModel = marketModel.getEvent();
    if (eventModel === null) {
        return null;
    }

    const isNonRunner = selectionModel.resultType === 'void';
    const isUnnamedFavourite = selectionModel.templateId === 'unnamed-favourite';

    const horseRacingRunner = getHorseRacingRunner(selectionModel, isUnnamedFavourite, false, isSpecials);

    // For boosted rows we show "Now" (boosted price) instead of SP.
    const spSelection =
        isPriceBoostActive === true
            ? getBoostedPrice(selectionModel, sportsbookVariant)
            : isUnnamedFavourite || breakpointsState.tablet.isBiggerOrEq === true
              ? getSP(selectionModel, sportsbookVariant)
              : null;

    if (type === undefined) {
        return (
            <RaceSummaryRowHorseRacing
                antePost={eventModel.antePost}
                dataTest='race-summary-row'
                isPriceBoostActive={isPriceBoostActive}
                isNonRunner={isNonRunner}
                isPriceHistoryHidden={isPriceHistoryHidden}
                isRaceInProgress={isRaceInProgress}
                isSpecials={isSpecials}
                isUnnamedFavourite={isUnnamedFavourite}
                runner={horseRacingRunner}
                selectionId={selectionId}
                sp={spSelection}
                winner={getWinner(selectionModel, sportsbookVariant, isPriceBoostActive)}
            />
        );
    }

    if (type === 'cast') {
        return (
            <RaceSummaryCastRowHorseRacing
                {...getTcFc(selectionModel, marketModel, sportsbookVariant)}
                dataTest='race-summary-cast-row'
                isNonRunner={isNonRunner}
                isUnnamedFavourite={isUnnamedFavourite}
                selectionId={selectionId}
            />
        );
    }
    return null;
});
